import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";

import { Strings } from "--root/strings/en";
import { Body2 } from "--components/typography";
import { SwaggerMutateClient, SwaggerQueryClient } from "--client/common/apis/rest";
import { Button } from "--floponents-web/atoms/Button";
import { SelectInput, tSelectValue } from "--floponents-web/atoms/SelectInput";
import { useToastErrorHandler } from "--floponents-web/molecules/ToastNotification/ToastProvider";

import { useAPICallState } from "../../../common/api/useAPICall";
import { cssBreakpoints } from "../../../../../../components/hooks/useBreakpoint";

const CemAttach = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${props => props.theme.spacing(2)};
`;
const CemAttachButton = styled(Button)`
    ${cssBreakpoints.mobile} {
        width: 100%;
    }
`;

export interface CemStatusDialogBodyAttachProps {
    streamId: string;
    spaceId: string;
}

export const CemStatusDialogBodyAttach = (props: CemStatusDialogBodyAttachProps) => {
    const err = useToastErrorHandler();
    const [working, setWorking] = useState(false);
    const [attachCemPlanId, setAttachCemPlanId] = useState<string>();
    const handleAttachCem = useCallback(() => {
        if (!attachCemPlanId) return;
        setWorking(true);
        err(SwaggerMutateClient.AttachCem({
            streamId: props.streamId,
            cemPlanId: attachCemPlanId
        })).catch(() => setWorking(false));
    }, [attachCemPlanId, err, props.streamId]);

    return (
        <CemAttach>
            <Body2>
                <Strings.Inline>
                    Attach
                    <CemPlanSelect
                        spaceId={props.spaceId}
                        onChange={setAttachCemPlanId}
                    />
                    to this stream.
                </Strings.Inline>
            </Body2>
            <CemAttachButton
                color="primary"
                onClick={handleAttachCem}
                disabled={
                    attachCemPlanId === undefined ||
                    working
                }
                indeterminate={working}
            >
                {Strings.inline("Attach")}
            </CemAttachButton>
        </CemAttach>
    );
};

const StyledCemPlanSelect = styled(SelectInput)`
    display: inline-block;
    margin: 0 ${props => props.theme.spacing(1)};
`;

interface CemPlanSelectProps {
    spaceId: string;
    onChange: (cemPlanId: string) => void;
}
const CemPlanSelect = (props: CemPlanSelectProps) => {
    const { onChange } = props;

    const [cemPlans] = useAPICallState(() => (
        SwaggerQueryClient.ListCemPlansForSpace({ spaceId: props.spaceId })
    ), [props.spaceId]);

    type Value = tSelectValue<NonNullable<typeof cemPlans>[number]>;

    const options: Array<Value> = useMemo(() => {
        return cemPlans?.length
            ? cemPlans.map(plan => ({
                key: plan,
                value: plan.name
            }))
            : [];
    }, [cemPlans]);

    const [value, setValue] = useState(() => (
        options.find(option => option.key.defaultFor) ?? options[0]
    ));

    const handleChange = useCallback((val: Value) => {
        setValue(val);
        onChange(val.key.id);
    }, [onChange]);

    return (
        <StyledCemPlanSelect
            options={options}
            value={value}
            onChange={handleChange}
            size="small"
            placeholder={Strings.inline("Select CEM plan")}
        />
    );
};
